import type { Photo, PhotoAsset, UploadJob } from '../types/contract';

const previewAssetOne: PhotoAsset = {
  id: 'asset-preview-1',
  photoId: 'photo-1',
  kind: 'preview',
  storagePath: 'user-1/photo-1/preview',
  width: 1600,
  height: 1200,
  bytes: 238400,
};

const previewAssetTwo: PhotoAsset = {
  id: 'asset-preview-2',
  photoId: 'photo-2',
  kind: 'preview',
  storagePath: 'user-1/photo-2/preview',
  width: 1600,
  height: 1067,
  bytes: 201210,
};

const previewAssetThree: PhotoAsset = {
  id: 'asset-preview-3',
  photoId: 'photo-3',
  kind: 'preview',
  storagePath: 'user-1/photo-3/preview',
  width: 1600,
  height: 1200,
  bytes: 198320,
};

export const mockPhotos: Photo[] = [
  {
    id: 'photo-1',
    userId: 'user-1',
    originalFilename: 'IMG_20260418_093400.jpg',
    mimeType: 'image/jpeg',
    capturedAt: '2026-04-18T09:34:00+09:00',
    latitude: 36.93972,
    longitude: 126.43154,
    altitudeM: 18,
    hasGps: true,
    status: 'ready',
    previewAssetPath: previewAssetOne.storagePath,
    thumbnailAssetPath: 'user-1/photo-1/thumbnail',
    createdAt: '2026-04-20T09:12:00+09:00',
    previewUrl:
      'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1200&q=80',
    note: '되메우기 전 배수관로 위치와 주변 지장물 상태를 기록했습니다.',
  },
  {
    id: 'photo-2',
    userId: 'user-1',
    originalFilename: 'KakaoTalk_20260419_140500123.jpg',
    mimeType: 'image/jpeg',
    capturedAt: '2026-04-19T14:05:00+09:00',
    latitude: 36.93993,
    longitude: 126.43179,
    altitudeM: 12,
    hasGps: true,
    status: 'ready',
    previewAssetPath: previewAssetTwo.storagePath,
    thumbnailAssetPath: 'user-1/photo-2/thumbnail',
    createdAt: '2026-04-20T09:18:00+09:00',
    previewUrl:
      'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1200&q=80',
    note: '통신관로 노출 위치와 깊이를 현장 확인용으로 남겼습니다.',
  },
  {
    id: 'photo-3',
    userId: 'user-1',
    originalFilename: 'IMG_20260420_105100.jpg',
    mimeType: 'image/jpeg',
    capturedAt: '2026-04-20T10:51:00+09:00',
    latitude: 36.51042,
    longitude: 126.34456,
    altitudeM: 9,
    hasGps: true,
    status: 'processing',
    previewAssetPath: previewAssetThree.storagePath,
    thumbnailAssetPath: 'user-1/photo-3/thumbnail',
    createdAt: '2026-04-20T09:31:00+09:00',
    previewUrl:
      'https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1200&q=80',
    note: '사진 압축과 썸네일 생성이 끝나면 지도에 반영됩니다.',
  },
  {
    id: 'photo-4',
    userId: 'user-1',
    originalFilename: 'KakaoTalk_20260416_130400777.jpg',
    mimeType: 'image/jpeg',
    capturedAt: '2026-04-16T13:04:00+09:00',
    latitude: null,
    longitude: null,
    altitudeM: null,
    hasGps: false,
    status: 'missing_gps',
    previewAssetPath: null,
    thumbnailAssetPath: null,
    createdAt: '2026-04-20T09:39:00+09:00',
    previewUrl: null,
    note: '사진은 저장됐지만 GPS가 없어 지도에는 표시되지 않습니다.',
  },
];

export const mockUploadJobs: UploadJob[] = [
  {
    id: 'job-1',
    userId: 'user-1',
    status: 'processing',
    totalCount: 12,
    successCount: 9,
    failedCount: 0,
    startedAt: '2026-04-20T08:55:00+09:00',
    finishedAt: null,
  },
  {
    id: 'job-2',
    userId: 'user-1',
    status: 'ready',
    totalCount: 3,
    successCount: 3,
    failedCount: 0,
    startedAt: '2026-04-20T07:20:00+09:00',
    finishedAt: '2026-04-20T07:23:00+09:00',
  },
];

export type ScenarioState = 'ready' | 'loading' | 'empty' | 'error';

export interface AppScenario {
  upload: { status: ScenarioState; helper: string };
  map: { status: ScenarioState };
  detail: { status: ScenarioState };
  photos: Photo[];
  jobs: UploadJob[];
  selectedPhotoId: string | null;
}

export const appScenarios: Record<ScenarioState, AppScenario> = {
  ready: {
    upload: { status: 'ready', helper: '현장 사진을 등록하면 GPS와 촬영 시각으로 위치 기록을 만듭니다.' },
    map: { status: 'ready' },
    detail: { status: 'ready' },
    photos: mockPhotos,
    jobs: mockUploadJobs,
    selectedPhotoId: 'photo-1',
  },
  loading: {
    upload: { status: 'loading', helper: '현장 사진과 메타데이터를 불러오는 중입니다.' },
    map: { status: 'loading' },
    detail: { status: 'loading' },
    photos: [],
    jobs: [],
    selectedPhotoId: null,
  },
  empty: {
    upload: { status: 'empty', helper: '아직 등록된 공사 사진이 없습니다.' },
    map: { status: 'empty' },
    detail: { status: 'empty' },
    photos: [],
    jobs: [],
    selectedPhotoId: null,
  },
  error: {
    upload: { status: 'error', helper: '현장 사진 기록을 불러오지 못했습니다. 잠시 후 다시 시도하세요.' },
    map: { status: 'error' },
    detail: { status: 'error' },
    photos: [],
    jobs: [],
    selectedPhotoId: null,
  },
};
